// src/services/api.js
import axios from 'axios';
import { API_URL } from '../config';
import { MAIL_API_URL, MAIL_API_KEY } from '../config';

// Axios instance oluştur
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor - her istekte token ekle
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - 401 hatalarını yakala
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Temp token'ları 401'de silmeyelim (OTP flow için)
      const tempToken = localStorage.getItem('tempToken');
      if (!tempToken) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API - OTP ENDPOİNT'LERİ EKLENDİ
export const authAPI = {
  // Eski login (backward compatibility)
  login: (username, password) =>
    api.post('/auth/login', { username, password }),

  // Legacy login (fallback)
  loginLegacy: (username, password) =>
    api.post('/auth/login-legacy', { username, password }),

  // OTP LOGIN FLOW
  loginRequest: (username, password) =>
    api.post('/auth/login-request', { username, password }),

  verifyOTP: (tempToken, otpCode) =>
    api.post('/auth/verify-otp', { tempToken, otpCode }),

  resendOTP: (tempToken) =>
    api.post('/auth/resend-otp', { tempToken }),

  linkTelegram: (connectionCode, telegramUserId, telegramUsername, firstName, lastName) =>
    api.post('/auth/link-telegram', {
      connectionCode,
      telegramUserId,
      telegramUsername,
      firstName,
      lastName
    }),

  checkTelegram: () =>
    api.post('/auth/check-telegram'),

  toggleOTP: (enabled) =>
    api.post('/auth/toggle-otp', { enabled }),

  // Mevcut metodlar
  changePassword: (currentPassword, newPassword) =>
    api.post('/auth/change-password', { currentPassword, newPassword }),

  getMe: () =>
    api.get('/auth/me'),

  logout: () =>
    api.post('/auth/logout'),

  verify: () =>
    api.get('/auth/verify'),

  // Admin/Root endpoints
  getUsers: () =>
    api.get('/auth/users'),

  createUser: (data) =>
    api.post('/auth/users', data),

  updateUser: (id, data) =>
    api.put(`/auth/users/${id}`, data),

  deleteUser: (id) =>
    api.delete(`/auth/users/${id}`),

  // Log endpoints (Root only)
  getActivityLogs: (params = {}) =>
    api.get('/auth/logs/activity', { params }),

  getLoginLogs: (params = {}) =>
    api.get('/auth/logs/login', { params }),

  getSystemLogs: (params = {}) =>
    api.get('/auth/logs/system', { params }),

  getOTPLogs: (params = {}) =>
    api.get('/auth/logs/otp', { params })
};

// Channels API
export const channelsAPI = {
  getAll: () => api.get('/channels'),

  getById: (id) => api.get(`/channels/${id}`),

  create: (data) => api.post('/channels', data),

  update: (id, data) => api.put(`/channels/${id}`, data),

  delete: (id) => api.delete(`/channels/${id}`),

  fetchInfo: (chatId) =>
    api.post('/channels/fetch-info', { chat_id: chatId }),

  updateInfo: (id) =>
    api.put(`/channels/${id}/update-info`),

  fetchAllInfo: () =>
    api.post('/channels/fetch-all-info'),

  updatePhoto: (id, formData) =>
    api.post(`/channels/${id}/update-photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  updateChannelInfo: (id, data) =>
    api.post(`/channels/${id}/update-channel-info`, data)
};

// Content API
export const contentAPI = {
  getAll: () => api.get('/content-library'),

  getById: (id) => api.get(`/content-library/${id}`),

  create: (formData) =>
    api.post('/content-library', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  update: (id, formData) =>
    api.put(`/content-library/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  delete: (id) => api.delete(`/content-library/${id}`),

  getHistory: (id) => api.get(`/content-library/${id}/history`)
};

// Posts API
export const postsAPI = {
  getAll: () => api.get('/posts'),

  getById: (id) => api.get(`/posts/${id}`),

  create: (formData) =>
    api.post('/posts', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  createFromLibrary: (data) =>
    api.post('/posts/from-library', data),

  send: (id) => api.post(`/posts/${id}/send`),

  delete: (id) => api.delete(`/posts/${id}`),

  getStats: (id) => api.get(`/posts/${id}/stats`)
};

// Tags API
export const tagsAPI = {
  getAll: () => api.get('/tags'),

  create: (data) => api.post('/tags', data),

  update: (id, data) => api.put(`/tags/${id}`, data),

  delete: (id) => api.delete(`/tags/${id}`)
};

// Automation API
export const automationAPI = {
  getRules: () => api.get('/automation/rules'),

  createRule: (data) => api.post('/automation/rules', data),

  updateRule: (id, data) => api.put(`/automation/rules/${id}`, data),

  deleteRule: (id) => api.delete(`/automation/rules/${id}`),

  toggleRule: (id) => api.post(`/automation/rules/${id}/toggle`),

  getSchedule: () => api.get('/automation/schedule'),

  runNow: (id) => api.post(`/automation/rules/${id}/run`),

  getHistory: (ruleId) => api.get(`/automation/history${ruleId ? `?rule_id=${ruleId}` : ''}`),

  getExecutionLogs: (params = {}) => api.get('/automation/execution-logs', { params })
};

// Statistics API
export const statisticsAPI = {
  getDashboard: () => api.get('/statistics/dashboard'),

  getChannelStats: (channelId, period = '7d') =>
    api.get(`/statistics/channels/${channelId}?period=${period}`),

  getContentStats: (contentId) =>
    api.get(`/statistics/content/${contentId}`),

  getAutomationStats: () =>
    api.get('/statistics/automation'),

  getOverview: () => api.get('/statistics/overview')
};

// MTProto API
export const mtprotoAPI = {
  getSession: () => api.get('/mtproto/session'),

  sendCode: (phoneNumber) =>
    api.post('/mtproto/send-code', { phoneNumber }),

  verifyCode: (phoneNumber, code, phoneCodeHash) =>
    api.post('/mtproto/verify-code', { phoneNumber, code, phoneCodeHash }),

  checkAuth: () => api.get('/mtproto/check-auth'),

  addBotToChannel: (chatId) =>
    api.post('/mtproto/add-bot-to-channel', { chat_id: chatId }),

  disconnect: () => api.post('/mtproto/disconnect'),

  getStatus: () => api.get('/mtproto/status')
};

// Users API (Admin panel)
export const usersAPI = {
  getAll: () => api.get('/users'),

  getById: (id) => api.get(`/users/${id}`),

  create: (data) => api.post('/users', data),

  update: (id, data) => api.put(`/users/${id}`, data),

  delete: (id) => api.delete(`/users/${id}`),

  getActivity: (userId) => api.get(`/users/${userId}/activity`)
};

// Logs API
export const logsAPI = {
  getActivity: (params = {}) => api.get('/logs/activity', { params }),

  getLogin: (params = {}) => api.get('/logs/login', { params }),

  getError: (params = {}) => api.get('/logs/error', { params }),

  getSystem: (params = {}) => api.get('/logs/system', { params }),

  getAudit: (params = {}) => api.get('/logs/audit', { params }),

  getPerformance: (params = {}) => api.get('/logs/performance', { params }),

  getSummary: () => api.get('/logs/summary')
};

// Quick Buttons API
export const quickButtonsAPI = {
  getAll: () => api.get('/quick-buttons'),

  create: (data) => api.post('/quick-buttons', data),

  update: (id, data) => api.put(`/quick-buttons/${id}`, data),

  delete: (id) => api.delete(`/quick-buttons/${id}`),

  reorder: (buttons) => api.put('/quick-buttons/reorder', { buttons })
};

// YouTube API
export const youtubeAPI = {
  // OAuth
  getAuthUrl: () => api.get('/youtube/oauth/auth-url'),

  callback: (code) => api.get(`/youtube/oauth/callback?code=${code}`),

  disconnect: () => api.post('/youtube/oauth/disconnect'),

  // Channels
  getChannels: () => api.get('/youtube/channels'),

  syncChannel: (channelId) => api.post(`/youtube/channels/${channelId}/sync`),

  // Videos
  uploadVideo: (formData) =>
    api.post('/youtube/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  getVideos: () => api.get('/youtube/videos'),

  deleteVideo: (videoId) => api.delete(`/youtube/videos/${videoId}`),

  // Statistics
  getStats: () => api.get('/youtube/statistics'),

  getVideoStats: (videoId) => api.get(`/youtube/videos/${videoId}/stats`)
};

// Instagram API - YENİ!
export const instagramAPI = {
  // Health Check
  health: () => api.get('/instagram/health'),

  // Authentication
  login: (username, password, channelName) =>
    api.post('/instagram/auth/login', { username, password, channelName }),

  logout: (channelId) =>
    api.post('/instagram/auth/logout', { channelId }),

  checkSession: (channelId) =>
    api.get(`/instagram/auth/check-session/${channelId}`),

  // Channels
  getChannels: () => api.get('/instagram/channels'),

  getChannel: (id) => api.get(`/instagram/channels/${id}`),

  createChannel: (data) => api.post('/instagram/channels', data),

  updateChannel: (id, data) => api.put(`/instagram/channels/${id}`, data),

  deleteChannel: (id) => api.delete(`/instagram/channels/${id}`),

  syncChannel: (channelId) => api.post(`/instagram/channels/${channelId}/sync`),

  // Posts
  getPosts: (channelId) => api.get(`/instagram/posts/${channelId}`),

  uploadPost: (formData) =>
    api.post('/instagram/posts/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  schedulePost: (data) => api.post('/instagram/posts/schedule', data),

  deletePost: (postId) => api.delete(`/instagram/posts/${postId}`),

  // Statistics
  getAccountStats: (channelId) =>
    api.get(`/instagram/stats/account/${channelId}`),

  getEngagementStats: (channelId) =>
    api.get(`/instagram/stats/engagement/${channelId}`),

  getDashboardStats: (channelId) =>
    api.get(`/instagram/stats/dashboard/${channelId}`),

  getPostStats: (postId) =>
    api.get(`/instagram/stats/post/${postId}`),

  // Media
  uploadMedia: (formData) =>
    api.post('/instagram/media/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  // Stories
  uploadStory: (formData) =>
    api.post('/instagram/stories/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  getStories: (channelId) => api.get(`/instagram/stories/${channelId}`)
};

// Quick Content Settings API
export const quickContentAPI = {
  getSettings: () => api.get('/quick-content-settings'),

  saveSettings: (settings) =>
    api.post('/quick-content-settings', { settings }),

  clearSettings: () =>
    api.post('/quick-content-settings', { settings: null })
};


export const channelForwardAPI = {
  // Tüm forward'ları getir
  getAll: () => api.get('/channel-forwards'),

  // Belirli kanal için forward'ları getir
  getByChannel: (channelId) => api.get(`/channel-forwards/channel/${channelId}`),

  // Kaynak kanalı doğrula
  validate: (username) => api.post('/channel-forwards/validate', { username }),

  // Yeni forward oluştur
  create: (data) => api.post('/channel-forwards', data),

  // Forward güncelle
  update: (id, data) => api.put(`/channel-forwards/${id}`, data),

  // Forward başlat
  start: (id) => api.post(`/channel-forwards/${id}/start`),

  // Forward durdur
  stop: (id) => api.post(`/channel-forwards/${id}/stop`),

  // Forward sil
  delete: (id) => api.delete(`/channel-forwards/${id}`),

  // Forward logları
  getLogs: (id, limit = 50) => api.get(`/channel-forwards/${id}/logs?limit=${limit}`),

  // Forward istatistikleri
  getStats: (id) => api.get(`/channel-forwards/${id}/stats`)
};

const mailApi = axios.create({
  baseURL: MAIL_API_URL,
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': MAIL_API_KEY
  }
});

export const mailAPI = {
  health: () => mailApi.get('/health'),
  getAccounts: () => mailApi.get('/api/mail-accounts'),
  getAccount: (id) => mailApi.get(`/api/mail-accounts/${id}`),
  createAccount: (data) => mailApi.post('/api/mail-accounts', data),
  updateAccount: (id, data) => mailApi.put(`/api/mail-accounts/${id}`, data),
  deleteAccount: (id) => mailApi.delete(`/api/mail-accounts/${id}`),
  getTemplates: () => mailApi.get('/api/templates'),
  getTemplatesByAccount: (accountId) => mailApi.get(`/api/templates/account/${accountId}`),
  createTemplate: (data) => mailApi.post('/api/templates', data),
  updateTemplate: (id, data) => mailApi.put(`/api/templates/${id}`, data),
  deleteTemplate: (id) => mailApi.delete(`/api/templates/${id}`),
  getLogs: (params = {}) => mailApi.get('/api/logs', { params }),
  getStats: () => mailApi.get('/api/logs/stats'),

  getInbox: (accountId) => mailApi.get(`/api/inbox/${accountId}/inbox`),
  getMessage: (accountId, messageId) => mailApi.get(`/api/inbox/${accountId}/messages/${messageId}`),
  deleteMessage: (accountId, messageId) => mailApi.delete(`/api/inbox/${accountId}/messages/${messageId}`)
};

export const instagramScrapeAPI = {
  preview: (url) => api.post('/instagramscrape/preview', { url }),

  download: (url) => api.post('/instagramscrape/download', { url }),

  transform: (imagePath, prompt) =>
    api.post('/instagramscrape/transform', { imagePath, prompt }),

  importAndTransform: (url, prompt) =>
    api.post('/instagramscrape/import', { url, prompt }),

  addToLibrary: (data) =>
    api.post('/instagramscrape/add-to-library', data),

  // Profil çekme
  profilePreview: (url) =>
    api.post('/instagramscrape/profile/preview', { url }),

  profilePosts: (url, maxId = null) =>
    api.post('/instagramscrape/profile/posts', { url, maxId }),

  profileImport: (url, tagIds) =>
    api.post('/instagramscrape/profile/import', { url, tagIds }),

  profileFetchAllPosts: (url) =>
    api.post('/instagramscrape/profile/fetch-all-posts', { url }),

  profileDownloadPost: (post, tagIds, username, startNumber, platformConfig) =>
    api.post('/instagramscrape/profile/download-post', { post, tagIds, username, startNumber, platformConfig }),

  profileHighlights: (url) =>
    api.post('/instagramscrape/profile/highlights', { url }),

  profileDownloadHighlight: (item, tagIds, username, number, platformConfig) =>
    api.post('/instagramscrape/profile/download-highlight', { item, tagIds, username, number, platformConfig })
}

export default api;
